/**
 * Bridge to ptengine.track() — queues events until ptengine.js loads,
 * polls up to 5 seconds before giving up.
 */
import { log } from './logger';

interface Pending {
  name: string;
  props?: Record<string, unknown>;
}

const queue: Pending[] = [];
let pollingTimer: ReturnType<typeof setInterval> | null = null;
let pollCount = 0;
const POLL_INTERVAL_MS = 200;
const MAX_POLLS = 25; // ≈ 5 seconds

function callTrack(name: string, props?: Record<string, unknown>): boolean {
  const pt = window.ptengine;
  if (!pt || typeof pt.track !== 'function') return false;
  try {
    if (props && Object.keys(props).length > 0) {
      pt.track(name, props);
    } else {
      pt.track(name);
    }
    return true;
  } catch (e) {
    log('warn', 'ptengine.track threw', e);
    return true; // count as delivered; do not retry
  }
}

function flushQueue(): void {
  while (queue.length > 0) {
    const item = queue[0];
    if (!callTrack(item.name, item.props)) return; // ptengine vanished mid-flush
    queue.shift();
  }
}

function startPolling(): void {
  if (pollingTimer) return;
  pollCount = 0;
  pollingTimer = setInterval(() => {
    pollCount += 1;
    if (window.ptengine && typeof window.ptengine.track === 'function') {
      flushQueue();
      clearInterval(pollingTimer!);
      pollingTimer = null;
      return;
    }
    if (pollCount >= MAX_POLLS) {
      log('warn', 'ptengine.js never appeared; dropping ' + queue.length + ' queued events');
      queue.length = 0;
      clearInterval(pollingTimer!);
      pollingTimer = null;
    }
  }, POLL_INTERVAL_MS);
}

export function trackPtengine(eventName: string, properties?: Record<string, unknown>): void {
  if (window.ptengine && typeof window.ptengine.track === 'function') {
    callTrack(eventName, properties);
    flushQueue();
    return;
  }
  queue.push({ name: eventName, props: properties });
  startPolling();
}

/**
 * Test-only: reset internal state.
 *
 * Do NOT call this from any production module — it is intentionally not
 * re-exported from src/index.ts so that tree-shaking can drop it from the
 * IIFE bundle (verified: bundle contains 0 references when only tests import).
 */
export function __resetTrackQueueForTests(): void {
  queue.length = 0;
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
  }
  pollCount = 0;
}
